import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TriggerDefinition,
  useAgentWorkflowTriggersStore,
} from "@/store/agentWorkflowTriggersStore";
import { SelectTriggerModal } from "@/modules/agents/components/SelectTriggerModal";
import {
  LIST_STATE_KEY,
  useStandardAgentTriggersStore,
} from "@/store/standardAgentTriggersStore";
import { StandardAgentTriggerEditorDialog } from "./StandardAgentTriggerEditorDialog";
import {
  CreateStandardAgentTriggerBody,
  StandardAgentTrigger,
  UpdateStandardAgentTriggerBody,
} from "@forgepoint/shared";

export function StandardAgentTriggersCard() {
  const triggerIds = useStandardAgentTriggersStore((s) => s.triggerIds);
  const triggerMap = useStandardAgentTriggersStore((s) => s.triggerMap);
  const fetchTriggers = useStandardAgentTriggersStore((s) => s.fetchTriggers);
  const createTrigger = useStandardAgentTriggersStore((s) => s.createTrigger);
  const updateTrigger = useStandardAgentTriggersStore((s) => s.updateTrigger);
  const deleteTrigger = useStandardAgentTriggersStore((s) => s.deleteTrigger);
  const getTriggerState = useStandardAgentTriggersStore(
    (s) => s.getTriggerState,
  );
  const listState = useStandardAgentTriggersStore((s) =>
    s.getTriggerState(LIST_STATE_KEY),
  );

  const workflowTriggerMap = useAgentWorkflowTriggersStore((s) => s.triggers);
  const fetchTriggersByIds = useAgentWorkflowTriggersStore(
    (s) => s.fetchTriggersByIds,
  );
  const getWorkflowTriggerById = useAgentWorkflowTriggersStore(
    (s) => s.getTriggerById,
  );

  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTriggerDefinition, setSelectedTriggerDefinition] =
    useState<TriggerDefinition | null>(null);
  const [editingTriggerId, setEditingTriggerId] = useState<string | null>(null);
  const [pendingDeleteTriggerId, setPendingDeleteTriggerId] = useState<
    string | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchTriggers();
  }, [fetchTriggers]);

  const standardAgentTriggers = useMemo(() => {
    return triggerIds
      .map((id) => triggerMap.get(id))
      .filter(
        (trigger): trigger is StandardAgentTrigger => trigger !== undefined,
      );
  }, [triggerIds, triggerMap]);

  useEffect(() => {
    const workflowTriggerIds = Array.from(
      new Set(standardAgentTriggers.map((trigger) => trigger.triggerId)),
    );

    if (workflowTriggerIds.length === 0) {
      return;
    }

    fetchTriggersByIds(workflowTriggerIds);
  }, [standardAgentTriggers, fetchTriggersByIds]);

  const editingTrigger = useMemo(() => {
    if (!editingTriggerId) {
      return null;
    }
    return triggerMap.get(editingTriggerId) ?? null;
  }, [editingTriggerId, triggerMap]);

  const handleSelectTrigger = (triggerDefinition: TriggerDefinition) => {
    setSelectedTriggerDefinition(triggerDefinition);
    setEditingTriggerId(null);
    setEditorOpen(true);
  };

  const handleEditTrigger = async (trigger: StandardAgentTrigger) => {
    let workflowTrigger = getWorkflowTriggerById(trigger.triggerId);
    if (!workflowTrigger) {
      await fetchTriggersByIds([trigger.triggerId]);
      workflowTrigger = getWorkflowTriggerById(trigger.triggerId);
    }

    if (!workflowTrigger) {
      toast.error("Unable to load trigger definition");
      return;
    }

    setSelectedTriggerDefinition(workflowTrigger);
    setEditingTriggerId(trigger.id);
    setEditorOpen(true);
  };

  const handleDeleteTrigger = async (triggerId: string) => {
    const deleted = await deleteTrigger(triggerId);
    if (deleted) {
      toast.success("Trigger deleted");
      return;
    }
    toast.error("Failed to delete trigger");
  };

  const handleSaveTrigger = async (
    payload: CreateStandardAgentTriggerBody | UpdateStandardAgentTriggerBody,
  ) => {
    setIsSaving(true);
    try {
      if (editingTriggerId) {
        const updated = await updateTrigger(editingTriggerId, payload);
        if (!updated) {
          toast.error("Failed to update trigger");
          return;
        }

        toast.success("Trigger updated");
      } else {
        const created = await createTrigger(payload);
        if (!created) {
          toast.error("Failed to create trigger");
          return;
        }

        toast.success("Trigger created");
      }

      setEditorOpen(false);
      setEditingTriggerId(null);
      setSelectedTriggerDefinition(null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Agent SDK Triggers</CardTitle>
              <CardDescription>
                Configure trigger automations for the standard agent.
              </CardDescription>
            </div>
            <Button type="button" onClick={() => setTriggerModalOpen(true)}>
              Add Trigger
              <Plus className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {listState.isLoadingGet ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading triggers</span>
            </div>
          ) : listState.getError ? (
            <p className="text-sm text-destructive">{listState.getError}</p>
          ) : standardAgentTriggers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No triggers configured.
            </p>
          ) : (
            standardAgentTriggers.map((trigger) => {
              const workflowTrigger = workflowTriggerMap.get(trigger.triggerId);
              const triggerState = getTriggerState(trigger.id);

              return (
                <div
                  key={trigger.id}
                  className="rounded-md border p-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {workflowTrigger?.name ?? trigger.triggerId}
                    </p>
                    <p className="text-xs text-muted-foreground break-words">
                      {workflowTrigger?.description ?? "No description"}
                    </p>
                    <p className="text-xs mt-2 text-muted-foreground">
                      User message:{" "}
                      {trigger.userMessage && trigger.userMessage.length > 0
                        ? trigger.userMessage
                        : "None"}
                    </p>
                    {triggerState.deleteError && (
                      <p className="text-xs text-destructive mt-1">
                        {triggerState.deleteError}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditTrigger(trigger)}
                      disabled={triggerState.isLoadingDelete}
                    >
                      Edit
                      <Pencil className="ml-2 h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingDeleteTriggerId(trigger.id)}
                      disabled={triggerState.isLoadingDelete}
                    >
                      Delete
                      {triggerState.isLoadingDelete ? (
                        <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="ml-2 h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <SelectTriggerModal
        open={triggerModalOpen}
        onOpenChange={setTriggerModalOpen}
        onSelectTrigger={handleSelectTrigger}
      />

      <StandardAgentTriggerEditorDialog
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) {
            setEditingTriggerId(null);
            setSelectedTriggerDefinition(null);
          }
        }}
        triggerDefinition={selectedTriggerDefinition}
        initialTrigger={editingTrigger}
        isSaving={isSaving}
        onSave={handleSaveTrigger}
      />

      <AlertDialog
        open={pendingDeleteTriggerId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteTriggerId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete trigger</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop future executions for this trigger.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingDeleteTriggerId) {
                  return;
                }
                handleDeleteTrigger(pendingDeleteTriggerId);
                setPendingDeleteTriggerId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
